import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import _, { differenceWith, isEqual } from 'lodash';
import { fetchFields } from 'slices/field.slice';
import {
  type StartAppListenerEffect,
  type StartAppListeningPredicate,
  startAppListening,
} from 'slices/listenerMiddleware';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IAPIResult,
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
import { isReadOnlyField } from 'utils/fieldUtils';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  NEW_OBJECT_ID,
  collectOrderedFieldIds,
  isNewObject,
  mergeFieldsForTypes,
} from 'utils/objectUtils';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { type TTEObject, createEmptyTEObject } from '@timeedit/types';
import type { ColumnConfig } from '@timeedit/ui-components';
import api from '../services/api.service';

export type TObjectsState = IDefaultReducerState & {
  map: Record<number, TTEObject>;
  allObjects: TTEObject[];
  isFirstObjectSaveDisabled: boolean;
  isSecondObjectSaveDisabled: boolean;
  firstObject?: TTEObject;
  firstObjectDirtyFields?: ObjectManagerDirtyFields;
  secondObject?: TTEObject;
  secondObjectDirtyFields?: ObjectManagerDirtyFields;
  findObjectPayload: TFindObjectsPayload;
  columnConfigsByTypeId: Record<string, ColumnConfig[]>;
};

/** Record of keys in object that has been changed since last save */
export type ObjectManagerDirtyFields = Record<string, string[]>;

export const initialState: TObjectsState = {
  loading: false,
  hasErrors: false,
  allObjects: [],
  map: {},
  isFirstObjectSaveDisabled: true,
  isSecondObjectSaveDisabled: true,
  firstObject: undefined,
  firstObjectDirtyFields: undefined,
  secondObject: undefined,
  secondObjectDirtyFields: undefined,
  findObjectPayload: {},
  columnConfigsByTypeId: {},
};

type SortField = 'ID' | 'MOD' | 'FIELD';
type SortDirection = 'ASC' | 'DESC';
type Sort = `${SortDirection}${SortField}`;

export type TFindObjectsPayload = {
  extIds?: string[];
  typeId?: string | number;
  ids?: number[];
  excludeIds?: number[];
  excludeExtIds?: string[];
  fields?: {
    exactSearchFields?: Array<{
      fieldId: number;
      values: string[];
    }>;
    // TODO: Figure out if string[] is valid or if it should be removed
    generalSearchFieldIds?: number[] | string[];
    searchText?: string;
  };
  modified?: {
    beginModified?: number;
    endModified?: number;
  };
  objectStatus?: 'READ' | 'RESERVE' | 'PLAN';
  abstract?: 'TRUE' | 'FALSE';
  active?: 'TRUE' | 'FALSE';
  virtual?: 'TRUE' | 'FALSE';
  limit?: number;
  page?: number;
  sort?: Sort;
  sortFieldId?: number;
  organizations?: string[];
  includeTemplateObjects?: boolean;
  includeReferenceFields?: boolean;
};

// Slice
const slice = createSlice({
  name: 'objects',
  initialState,
  reducers: {
    fetchObjectsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchObjectsSuccess: (
      state: TObjectsState,
      { payload }: PayloadAction<Pick<IDefaultAPIResult<TTEObject>, 'results'>>
    ) => {
      state.allObjects = payload.results;
      state.map = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchObjectsByIdsSuccess: (
      state: TObjectsState,
      { payload }: PayloadAction<Pick<IDefaultAPIResult<TTEObject>, 'results'>>
    ) => {
      payload.results.forEach((result) => {
        state.map[result.id] = result;
      });
      finishedLoadingSuccess(state);
    },
    fetchMoreObjectSuccess: (
      state: TObjectsState,
      { payload }: PayloadAction<Pick<IDefaultAPIResult<TTEObject>, 'results'>>
    ) => {
      state.allObjects = [...state.allObjects, ...payload.results];
      payload?.results?.forEach((result: TTEObject) => {
        state.map[result.id] = result;
      });

      finishedLoadingSuccess(state);
    },
    fetchObjectsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteObjectRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteObjectSuccess: (
      state: TObjectsState,
      { payload: objectId }: PayloadAction<number>
    ) => {
      state.allObjects = state.allObjects.filter(
        (object: TTEObject) => object.id !== objectId
      );
      closeDrawerById(state, objectId);
      finishedLoadingSuccess(state);
    },
    deleteObjectFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateObjectRequest: (state) => {
      isLoadingRequest(state);
    },
    updateObjectSuccess: (
      state: TObjectsState,
      { payload: objectId }: PayloadAction<number>
    ) => {
      closeDrawerById(state, objectId);
      finishedLoadingSuccess(state);
    },
    updateObjectFailure: (state) => {
      finishedLoadingFailure(state);
    },
    changeObject: (
      state: TObjectsState,
      {
        payload,
      }: PayloadAction<{ isFirstDrawer: boolean; object: Partial<TTEObject> }>
    ) => {
      const { isFirstDrawer, object } = payload;
      const objectId =
        payload.object.id ??
        (isFirstDrawer ? state.firstObject?.id : state.secondObject?.id);
      const originalObject = state.map[objectId];

      if (isFirstDrawer) {
        state.firstObjectDirtyFields = getUpdatedDirtyFields(
          object,
          originalObject,
          state.firstObjectDirtyFields ?? {}
        );

        state.firstObject = {
          ...state.firstObject,
          ...object,
        };
      } else {
        state.secondObjectDirtyFields = getUpdatedDirtyFields(
          object,
          originalObject,
          state.secondObjectDirtyFields ?? {}
        );

        state.secondObject = {
          ...state.secondObject,
          ...object,
        };
      }
    },
    resetObject: (
      state: TObjectsState,
      { payload }: PayloadAction<{ isFirstDrawer: boolean }>
    ) => {
      if (payload.isFirstDrawer) {
        state.firstObject = state.map[state.firstObject.id];
        state.firstObjectDirtyFields = undefined;
      } else {
        state.secondObject = state.map[state.secondObject.id];
        state.secondObjectDirtyFields = undefined;
      }
    },
    closeDrawer: (
      state: TObjectsState,
      { payload }: PayloadAction<{ isFirstDrawer: boolean }>
    ) => {
      if (payload.isFirstDrawer) {
        if (isNewObject(state.firstObject)) {
          state.map = _.omit(state.map, state.firstObject?.id);
          state.allObjects = state.allObjects.filter(
            (object: TTEObject) => object.id !== state.firstObject?.id
          );
        }
        state.firstObject = undefined;
        state.firstObjectDirtyFields = undefined;
      } else {
        state.secondObject = undefined;
        state.secondObjectDirtyFields = undefined;
      }
    },
    setIsObjectSaveDisabled: (
      state,
      {
        payload: { isFirstDrawer, object },
      }: PayloadAction<{
        isFirstDrawer: boolean;
        object?: TTEObject;
      }>
    ) => {
      const property = isFirstDrawer
        ? 'isFirstObjectSaveDisabled'
        : 'isSecondObjectSaveDisabled';

      if (!object) {
        state[property] = true;
        return;
      }

      state[property] = checkSavedDisabled(object, state.map[object.id]);
    },
    setFirstObject: (
      state: TObjectsState,
      { payload: objectId }: PayloadAction<number>
    ) => {
      state.firstObject = state.map[objectId];
    },
    setSecondObject: (
      state: TObjectsState,
      { payload: objectId }: PayloadAction<number>
    ) => {
      state.secondObject = state.map[objectId];
    },
    duplicateObject: (
      state: TObjectsState,
      { payload: objectId }: PayloadAction<number>
    ) => {
      const selectedObject = state.map[objectId];
      const newObject: TTEObject = {
        ...selectedObject,
        id: NEW_OBJECT_ID,
        extId: selectedObject.extId + '-copy',
      };
      state.firstObject = insertNewObject(state, newObject);
    },
    createUnsavedObject: (
      state: TObjectsState,
      { payload }: PayloadAction<{ typeId: number; fieldIds: number[] }>
    ) => {
      state.firstObject = insertNewObject(
        state,
        createEmptyTEObject(payload.typeId, payload.fieldIds)
      );
    },
    setFindObjectPayload: (
      state: TObjectsState,
      { payload }: PayloadAction<TFindObjectsPayload>
    ) => {
      if (!_.isEqual(payload, state.findObjectPayload)) {
        state.findObjectPayload = payload;
      }
    },
    saveColumnConfigs: (
      state,
      {
        payload,
      }: PayloadAction<{
        typeId: string;
        columnConfigs: ColumnConfig[];
      }>
    ) => {
      state.columnConfigsByTypeId[payload.typeId] = payload.columnConfigs;
    },
  },
  selectors: {
    isFirstObjectSaveDisabledSelector: (state) =>
      state.isFirstObjectSaveDisabled,
    isSecondObjectSaveDisabledSelector: (state) =>
      state.isSecondObjectSaveDisabled,
  },
});

/**
 * Adds new object to state, replacing existing object with same ID if found.
 */
const insertNewObject = (state: TObjectsState, object: TTEObject) => {
  const existingIndex = state.allObjects.findIndex((o) => o.id === object.id);
  if (existingIndex > -1) {
    state.allObjects[existingIndex] = object;
  } else {
    state.allObjects.unshift(object);
  }
  state.map[object.id] = object;
  return object;
};

const closeDrawerById = (state: TObjectsState, objectId: number) => {
  if (state.firstObject?.id === objectId) {
    state.firstObject = undefined;
    state.firstObjectDirtyFields = undefined;
  } else {
    state.secondObject = undefined;
    state.secondObjectDirtyFields = undefined;
  }
};

// Thunk: advanced update including dynamic reconciliation of fields based on selected types
export const advanceUpdateObject =
  (patch: { isFirstDrawer: boolean; object: Partial<TTEObject> }): TAppThunk =>
  (dispatch, getState) => {
    const { isFirstDrawer, object } = patch;

    // If types not part of patch just do a shallow change
    if (!object.types) {
      dispatch(changeObject({ isFirstDrawer, object }));
      return;
    }

    const state = getState();
    const { objectTypes, fields, objects } = state;

    const orderedFieldIds = collectOrderedFieldIds(
      object.types,
      objectTypes.map,
      fields.globalOrder
    );

    const baseObject = isFirstDrawer
      ? objects.firstObject
      : objects.secondObject;
    if (!baseObject) {
      // Nothing to merge against yet
      dispatch(changeObject({ isFirstDrawer, object }));
      return;
    }
    const originalObject = objects.map[baseObject.id];

    const mergedFields = mergeFieldsForTypes(
      orderedFieldIds,
      baseObject.fields,
      originalObject?.fields,
      fields.fieldMapping
    );

    dispatch(
      changeObject({
        isFirstDrawer,
        object: { ...object, fields: mergedFields },
      })
    );
  };

export default slice.reducer;

// Selectors
export const objectsSelector = (state: TRootState) => state.objects.allObjects;
export const objectMapSelector = (state: TRootState) => state.objects.map;
export const objectMapByExtIdSelector = createSelector(
  (state: TRootState) => state.objects.map,
  (objMap) => _.chain(objMap).values().keyBy('extId').value()
);

export const firstObjectSelector = (state: TRootState) =>
  state.objects.firstObject;
export const secondObjectSelector = (state: TRootState) =>
  state.objects.secondObject;
export const findObjectPayloadSelector = (state: TRootState) =>
  state.objects.findObjectPayload;
export const columnConfigsByTypeIdSelector = (state: TRootState) =>
  state.objects.columnConfigsByTypeId;
export const firstObjectDirtyFieldsSelector = (state: TRootState) =>
  state.objects.firstObjectDirtyFields;
export const secondObjectDirtyFieldsSelector = (state: TRootState) =>
  state.objects.secondObjectDirtyFields;

// Actions
export const {
  fetchObjectsRequest,
  fetchObjectsSuccess,
  fetchObjectsFailure,
  deleteObjectRequest,
  deleteObjectSuccess,
  deleteObjectFailure,
  updateObjectRequest,
  updateObjectSuccess,
  updateObjectFailure,
  changeObject,
  resetObject,
  closeDrawer,
  setIsObjectSaveDisabled,
  setFirstObject,
  setSecondObject,
  duplicateObject,
  createUnsavedObject,
  setFindObjectPayload,
  fetchMoreObjectSuccess,
  fetchObjectsByIdsSuccess,
  saveColumnConfigs,
} = slice.actions;

export const {
  isFirstObjectSaveDisabledSelector,
  isSecondObjectSaveDisabledSelector,
} = slice.selectors;

export const findObjects = createAppAsyncThunk<
  IDefaultAPIResult<TTEObject> | void,
  TFindObjectsPayload
>('objects/findObjects', async (body, { dispatch }) => {
  try {
    dispatch(fetchObjectsRequest());
    const objects = await findMoreObjects(body);
    dispatch(fetchObjectsSuccess(objects));
    return objects;
  } catch (e) {
    dispatch(fetchObjectsFailure());
  }
});

export const findObjectsByIds = createAppAsyncThunk<
  IDefaultAPIResult<TTEObject> | void,
  { ids: number[] }
>('objects/findObjectsByIds', async ({ ids }, { dispatch }) => {
  try {
    dispatch(fetchObjectsRequest());
    const chunkObjectIds = _.chunk(ids, 1000);
    for (const chunk of chunkObjectIds) {
      const objects = await findMoreObjects({ ids: chunk });
      dispatch(fetchObjectsByIdsSuccess(objects));
    }
  } catch (e) {
    dispatch(fetchObjectsFailure());
  }
});

export const findMoreObjects = async (
  body: TFindObjectsPayload
): Promise<IAPIResult<TTEObject>> => {
  try {
    return (await api.post({
      endpoint: '/objects/find',
      data: body,
      suppressNotification: true,
    })) as IAPIResult<TTEObject>;
  } catch (err) {
    console.error(err);
    return {
      results: [],
      page: 1,
      totalPages: 1,
      totalResults: 0,
      limit: 0,
    };
  }
};

export const deleteObject = createAppAsyncThunk<void, number>(
  'objects/deleteObject',
  async (objectId, { dispatch }) => {
    try {
      dispatch(deleteObjectRequest());
      await api.delete({
        endpoint: `/objects/${objectId}`,
      });
      dispatch(deleteObjectSuccess(objectId));
    } catch (e) {
      dispatch(deleteObjectFailure());
      return console.error(e);
    }
  }
);

export const updateObject = createAppAsyncThunk<void, TTEObject>(
  'objects/updateObject',
  async (object, { dispatch, getState }) => {
    try {
      dispatch(updateObjectRequest());
      const fieldMap = getState().fields.fieldMapping;
      const {
        id,
        history,
        created,
        createdBy,
        virtual,
        abstract,
        color,
        ...objectBody
      } = object;
      // TODO: Not sure if is where we should make sure that we only send the fields that are not empty and make sure they're strings
      const newFields = objectBody.fields
        // The object carries read-only reference fields whenever it was loaded with
        // `includeReferenceFields`; they must never reach a create/update payload.
        .filter((field) => !isReadOnlyField(fieldMap[field.fieldId]))
        .map((field) => ({
          ...field,
          values: field.values.filter((val) => val != null).map(String),
        }))
        .filter((field) => field.values.length > 0 && field.values[0] !== '');
      if (id === NEW_OBJECT_ID) {
        await api.post({
          endpoint: `/objects`,
          data: { ...objectBody, fields: newFields },
        });
      } else {
        await api.patch({
          endpoint: `/objects/${id}`,
          data: { ...objectBody, fields: newFields },
        });
      }
      dispatch(updateObjectSuccess(id));
      const { objects } = getState();
      dispatch(findObjects(objects.findObjectPayload));
      dispatch(fetchFields());
    } catch (e) {
      dispatch(updateObjectFailure());
      return console.error(e);
    }
  }
);

export const checkFirstObjectExists = (): TAppThunk<boolean> => (_, getState) =>
  !!getState().objects.firstObject;

export const checkObjectSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { objects } = getState();
    return isFirstDrawer
      ? objects.isFirstObjectSaveDisabled
      : objects.isSecondObjectSaveDisabled;
  };

type SelectedObjectKey = 'firstObject' | 'secondObject';

const selectedObjectPredicate =
  (key: SelectedObjectKey): StartAppListeningPredicate =>
  (_, currentState, previousState) =>
    currentState.objects[key] !== previousState.objects[key];

const selectedObjectEffect =
  (key: SelectedObjectKey): StartAppListenerEffect =>
  (_, { dispatch, getState }) => {
    dispatch(
      setIsObjectSaveDisabled({
        isFirstDrawer: key === 'firstObject',
        object: getState().objects[key],
      })
    );
  };

const getUpdatedDirtyFields = (
  newObject: Partial<TTEObject>,
  originalObject: TTEObject,
  dirtyFields: ObjectManagerDirtyFields
) => {
  const newDirtyFields: ObjectManagerDirtyFields = { ...dirtyFields };

  newDirtyFields.members =
    getDirtyFields(newObject.members, originalObject?.members, (m) =>
      m.objectId.toString()
    ) ?? newDirtyFields.members;

  newDirtyFields.relations =
    getDirtyFields(newObject.relations, originalObject?.relations, (r) =>
      r.objectIds.map((id) => id.toString()).join(',')
    ) ?? newDirtyFields.relations;

  newDirtyFields.availabilityRelations =
    getDirtyFields(
      newObject.availabilityRelations?.flatMap(
        (relation) => relation.objectIds
      ),
      originalObject?.availabilityRelations?.flatMap(
        (relation) => relation.objectIds
      ),
      (ar) => ar.toString()
    ) ?? newDirtyFields.availabilityRelations;

  newDirtyFields.optionalRelations =
    getDirtyFields(
      newObject.optionalRelations,
      originalObject?.optionalRelations,
      (id) => id.toString()
    ) ?? newDirtyFields.optionalRelations;

  return newDirtyFields;
};

// `transformFn` must produce a stable identity key — two items with the same key are treated as
// equal. Encode any equality-relevant attributes (e.g. `start`/`end`) into the key if they should
// affect dirty-tracking. Set-based lookup keeps this O(n+m) on large arrays.
const getDirtyFields = <T>(
  newItems: T[] | undefined,
  existingItems: T[] | undefined,
  transformFn: (item: T) => string
): string[] | undefined => {
  const news = newItems ?? [];
  const existings = existingItems ?? [];

  const existingKeys = new Set(existings.map(transformFn));
  const differences = news
    .map(transformFn)
    .filter((key) => !existingKeys.has(key));

  return differences.length > 0 ? differences : undefined;
};

startAppListening({
  predicate: selectedObjectPredicate('firstObject'),
  effect: selectedObjectEffect('firstObject'),
});

startAppListening({
  predicate: selectedObjectPredicate('secondObject'),
  effect: selectedObjectEffect('secondObject'),
});
